/* eslint-disable i18next/no-literal-string */
import type { IRawStyle } from '@fluentui/react';
import { FontWeights, IFontStyles } from '@fluentui/react';
import { mergeWith } from 'lodash';

export const variants = [
  'Caption2',
  'Caption2Strong',
  'Caption1',
  'Caption1Strong',
  'Body1',
  'Body1Strong',
  'Body2',
  'Subtitle2',
  'Subtitle1',
  'Title3',
  'Title2',
  'Title2Strong',
  'Title1',
  'Title1Strong',
  'LargeTitle',
  'Display',
] as const;

export type AppFontVariant = (typeof variants)[number];

export function isAppFontVariant(variant: AppFontVariant | keyof IFontStyles): variant is AppFontVariant {
  return (variants as ReadonlyArray<string>).includes(variant);
}

export type AppFontStyles = Record<AppFontVariant, IRawStyle>;

const { bold, semibold, regular } = FontWeights;
const defaultAppFonts: AppFontStyles = {
  Caption1: {
    fontSize: '1rem',
    lineHeight: '1.4rem',
    fontWeight: regular,
  },
  Caption1Strong: {
    fontSize: '1rem',
    lineHeight: '1.4rem',
    fontWeight: semibold,
  },
  Caption2: {
    fontSize: '1.2rem',
    lineHeight: '1.6rem',
    fontWeight: regular,
  },
  Caption2Strong: {
    fontSize: '1.2rem',
    lineHeight: '1.6rem',
    fontWeight: semibold,
  },
  Body1: {
    fontSize: '1.4rem',
    lineHeight: '2rem',
    fontWeight: regular,
  },
  Body2: {
    fontSize: '1.2rem',
    lineHeight: '1.6rem',
    fontWeight: regular,
  },
  Body1Strong: {
    fontSize: '1.4rem',
    lineHeight: '2rem',
    fontWeight: semibold,
  },
  Subtitle2: {
    fontSize: '1.6rem',
    lineHeight: '2.2rem',
    fontWeight: semibold,
  },
  Subtitle1: {
    fontSize: '2rem',
    lineHeight: '2.6rem',
    fontWeight: semibold,
  },
  Title3: {
    fontSize: '2.4rem',
    lineHeight: '3.2rem',
    fontWeight: semibold,
  },
  Title2: {
    fontSize: '2.8rem',
    lineHeight: '3.6rem',
    fontWeight: semibold,
  },
  Title2Strong: {
    fontSize: '1.6rem',
    lineHeight: '2.2rem',
    fontWeight: semibold,
  },
  // TODO: this font size may be too big, compared to Title1Strong
  Title1: {
    fontSize: '3.2rem',
    lineHeight: '4rem',
    fontWeight: semibold,
  },
  // Verified to V2 Defender Styles on 2/21
  Title1Strong: {
    fontSize: '2.4rem',
    lineHeight: '3.2rem',
    fontWeight: bold,
  },
  // Verified to V2 Defender styles
  LargeTitle: {
    fontSize: '3.2rem',
    lineHeight: '3.6rem',
    fontWeight: bold,
  },
  Display: {
    fontSize: '6.8rem',
    lineHeight: '9.2rem',
    fontWeight: semibold,
  },
};

export function createAppFontStyles(partialFontStyles?: Partial<AppFontStyles>): AppFontStyles {
  return mergeWith(defaultAppFonts, partialFontStyles);
}
